import { Canvas2dUnavailableError, createCanvas2d, type Canvas2d } from '../../../../../lib/browser/canvas';
import { fitWithin, IMAGE_MAX_LONG_EDGE, releaseCanvas } from '../../../../../lib/media/compressImage';

import { READY_STATE_HAVE_CURRENT_DATA, type MediaController } from '../media/useMediaController';

const MS_PER_SECOND = 1000;
/** The readability probe needs a single pixel. */
const PROBE_SIZE_PX = 1;
/** A seek that never reports `seeked` must not hold a capture forever. */
export const SEEK_SETTLE_TIMEOUT_MS = 2000;

export class NotReadyError extends Error {
  constructor() {
    super('video is not ready to capture');
    this.name = 'NotReadyError';
  }
}

export class CanvasTaintedError extends Error {
  constructor() {
    super('video pixels are not readable (cross-origin)');
    this.name = 'CanvasTaintedError';
  }
}

export interface CapturedFrame {
  bitmap: ImageBitmap;
  width: number;
  height: number;
  /** Playhead at capture, in ms. */
  tMs: number;
}

function reportTaint(): void {
  console.warn('[notes] canvas tainted: capture disabled');
}

/** Null when the browser has no 2d canvas at all — a verdict for the probe, not a failure. */
function canvasOrNull(width: number, height: number): Canvas2d | null {
  try {
    return createCanvas2d(width, height);
  } catch (err) {
    if (err instanceof Canvas2dUnavailableError) return null;
    throw err;
  }
}

/**
 * Can the browser read this video's pixels? Draws one pixel and tries to
 * read it back; a cross-origin video without CORS headers throws a
 * SecurityError here. Run once after the video is ready.
 */
export async function probeCanvasReadable(video: HTMLVideoElement): Promise<boolean> {
  const probe = canvasOrNull(PROBE_SIZE_PX, PROBE_SIZE_PX);
  if (!probe) return false;
  try {
    probe.ctx.drawImage(video, 0, 0, PROBE_SIZE_PX, PROBE_SIZE_PX);
    probe.ctx.getImageData(0, 0, PROBE_SIZE_PX, PROBE_SIZE_PX);
    return true;
  } catch {
    reportTaint();
    return false;
  }
}

/** Resolves once an in-flight seek lands, or after a bound so a stuck one can't hang the capture. */
function seekSettled(video: HTMLVideoElement): Promise<void> {
  if (!video.seeking) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const settle = () => {
      clearTimeout(timeoutId);
      video.removeEventListener('seeked', settle);
      resolve();
    };
    const timeoutId = setTimeout(settle, SEEK_SETTLE_TIMEOUT_MS);
    video.addEventListener('seeked', settle);
  });
}

/** Mid-seek, or before the current frame's data has arrived, `drawImage` paints nothing. */
function hasDrawableFrame(video: HTMLVideoElement): boolean {
  return !video.seeking && video.readyState >= READY_STATE_HAVE_CURRENT_DATA;
}

/**
 * Freeze the player and copy the presented frame into a bitmap at the
 * video's intrinsic size (long edge capped). Pauses first, lets any seek in
 * flight land and waits for a frame to actually be presented, so a mid-seek
 * capture never draws a stale or empty picture.
 */
export async function captureFrame(
  controller: MediaController,
  opts: { maxLongEdge?: number } = {},
): Promise<CapturedFrame> {
  const video = controller.videoRef.current;
  const { intrinsic } = controller;
  if (!video || !controller.ready || !intrinsic) {
    throw new NotReadyError();
  }
  controller.pause();
  await seekSettled(video);
  await controller.waitForPresentedFrame();
  if (!hasDrawableFrame(video)) throw new NotReadyError();

  const { width, height } = fitWithin(intrinsic.width, intrinsic.height, opts.maxLongEdge ?? IMAGE_MAX_LONG_EDGE);
  const { canvas, ctx } = createCanvas2d(width, height);
  try {
    ctx.drawImage(video, 0, 0, width, height);
    const bitmap = await createImageBitmap(canvas);
    return { bitmap, width, height, tMs: Math.round(video.currentTime * MS_PER_SECOND) };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'SecurityError') {
      reportTaint();
      throw new CanvasTaintedError();
    }
    throw err;
  } finally {
    // The bitmap holds its own pixels; the canvas is done.
    releaseCanvas(canvas);
  }
}
